import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CloseIcon from '@mui/icons-material/Close'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import {
  Alert,
  AlertTitle,
  alpha,
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  Link,
  Stack,
  type Theme,
  Typography,
} from '@mui/material'
import { type DragEvent, type FC, useCallback, useState } from 'react'
import { type FileRejection, useDropzone } from 'react-dropzone'
import { useSnackbar } from 'src/components/snackbar/useSnackbar'
import { getMBSize } from 'src/exShared/util/fileSize'
import { isHTTPError } from 'src/state/middleware/saga/handleHttpError'
import { useUploadGlossaryCsv } from '../api/useUploadGlossaryCsv'
import { useDownloadSampleCsv } from '../downloadSampleCsv'
import { UploadConfirmDialog } from './UploadConfirmDialog'

const GLOSSARY_MAX_FILE_SIZE_MB = 10
const GLOSSARY_MAX_FILE_SIZE = getMBSize(GLOSSARY_MAX_FILE_SIZE_MB)

type Props = {
  open: boolean
  onClose: () => void
  onOpenStatusDrawer: () => void
  onDownloadRegistered: () => void
  isDownloadingRegistered?: boolean
  isDownloadRegisteredDisabled?: boolean
}

export const CsvUploadDrawer: FC<Props> = ({
  open,
  onClose,
  onOpenStatusDrawer,
  onDownloadRegistered,
  isDownloadingRegistered = false,
  isDownloadRegisteredDisabled = false,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [rejectionError, setRejectionError] = useState<string | null>(null)
  const [dragFileCount, setDragFileCount] = useState(0)
  const [isCompleted, setIsCompleted] = useState(false)
  const downloadSampleCsv = useDownloadSampleCsv()
  const { showSuccessSnackbar, showErrorSnackbar } = useSnackbar()

  const uploadMutation = useUploadGlossaryCsv({
    onSuccess: () => {
      setSelectedFile(null)
      setIsCompleted(true)
      showSuccessSnackbar('用語集のアップロードを開始しました')
    },
    onError: error => {
      const detail = isHTTPError(error)
        ? error.errors.join('\n')
        : 'ネットワーク環境をご確認の上、再度アップロードしてください。'
      showErrorSnackbar(
        <Box sx={{ whiteSpace: 'pre-line' }}>
          {`アップロードに失敗しました。\n${detail}`}
        </Box>,
      )
    },
  })

  const validateUtf8 = useCallback(async (file: File): Promise<boolean> => {
    try {
      const head = file.slice(0, 8192)
      const buffer = await head.arrayBuffer()
      const decoder = new TextDecoder('utf-8', { fatal: true })
      decoder.decode(buffer, { stream: true })
      return true
    } catch {
      return false
    }
  }, [])

  const onDrop = useCallback(
    async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      const totalFiles = acceptedFiles.length + fileRejections.length
      if (totalFiles > 1) {
        setRejectionError('アップロードできるファイルは1つだけです')
        return
      }
      if (fileRejections.length > 0) {
        const hasFileTooLarge = fileRejections.some(r =>
          r.errors.some(e => e.code === 'file-too-large'),
        )
        if (hasFileTooLarge) {
          setRejectionError(
            `ファイルサイズが${GLOSSARY_MAX_FILE_SIZE_MB}MBの上限を超えています`,
          )
        } else {
          setRejectionError('CSVファイルのみアップロードできます')
        }
        return
      }
      const file = acceptedFiles[0]
      if (file) {
        const isUtf8 = await validateUtf8(file)
        if (!isUtf8) {
          setRejectionError('UTF-8のファイルのみアップロードできます')
          return
        }
        setSelectedFile(file)
        setRejectionError(null)
        uploadMutation.reset()
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- uploadMutation is unstable reference
    [uploadMutation.reset, validateUtf8],
  )

  const onDragEnter = useCallback((event: DragEvent) => {
    setDragFileCount(event.dataTransfer.items.length)
    setRejectionError(null)
  }, [])

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragReject,
    open: openFileDialog,
  } = useDropzone({
    onDrop,
    onDragEnter,
    accept: { 'text/csv': ['.csv'] },
    maxSize: GLOSSARY_MAX_FILE_SIZE,
    multiple: false,
    noClick: true,
    disabled: uploadMutation.isPending,
  })

  const handleExecuteClick = useCallback(() => {
    if (!selectedFile) return
    setConfirmDialogOpen(true)
  }, [selectedFile])

  const handleConfirmUpload = useCallback(() => {
    if (!selectedFile) return
    setConfirmDialogOpen(false)
    uploadMutation.mutate(selectedFile)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- uploadMutation is unstable reference
  }, [selectedFile, uploadMutation.mutate])

  const handleCancelConfirm = useCallback(() => {
    setConfirmDialogOpen(false)
  }, [])

  const handleClose = useCallback(() => {
    if (uploadMutation.isPending) return
    setSelectedFile(null)
    setRejectionError(null)
    setIsCompleted(false)
    uploadMutation.reset()
    onClose()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- uploadMutation is unstable reference
  }, [uploadMutation.isPending, uploadMutation.reset, onClose])

  const dropzoneSx = {
    border: '2px dashed',
    borderRadius: 1,
    p: 3,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 1.5,
    transition: 'border-color 0.2s, background-color 0.2s',
    // 状態別スタイル
    ...(isDragReject
      ? {
          borderColor: 'error.main',
          bgcolor: 'transparent',
          cursor: 'no-drop',
        }
      : isDragActive
        ? {
            borderColor: 'primary.main',
            bgcolor: (theme: Theme) => alpha(theme.palette.primary.main, 0.08),
          }
        : {
            borderColor: 'action.disabled',
            bgcolor: 'transparent',
          }),
  } as const

  return (
    <>
      <Drawer anchor="right" open={open} onClose={handleClose}>
        <Box
          sx={{
            width: 600,
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <Typography
            variant="h6"
            sx={{ pb: 2, borderBottom: 1, borderColor: 'divider', mb: 3 }}
          >
            用語集をアップロード(CSV)
          </Typography>

          {isCompleted ? (
            <>
              <Stack
                spacing={2}
                alignItems="center"
                justifyContent="center"
                sx={{ flex: 1 }}
              >
                <CheckCircleIcon color="success" sx={{ fontSize: 48 }} />
                <Typography variant="h6" fontWeight="bold">
                  データの更新を受け付けました
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  更新には最大10分程度かかります。しばらくお待ちください。
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ※この画面やブラウザを閉じても処理は継続されます
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => {
                    handleClose()
                    onOpenStatusDrawer()
                  }}
                >
                  データ処理状況を見る
                </Button>
              </Stack>

              <Stack
                direction="row"
                spacing={2}
                justifyContent="flex-end"
                sx={{ pt: 3 }}
              >
                <Button onClick={handleClose} variant="outlined">
                  閉じる
                </Button>
              </Stack>
            </>
          ) : (
            <>
              <Stack spacing={3} sx={{ flex: 1 }}>
                <Alert severity="info" variant="outlined">
                  <AlertTitle>アップロードの手順</AlertTitle>
                  <Box
                    component="ol"
                    sx={{
                      m: 0,
                      pl: 3,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5,
                    }}
                  >
                    <Box component="li">
                      <Typography variant="body2" fontWeight="bold">
                        記入用ファイルを準備する
                      </Typography>
                      <Typography variant="body2">
                        [新規登録]{' '}
                        <Link
                          component="button"
                          variant="body2"
                          onClick={downloadSampleCsv}
                          sx={{ verticalAlign: 'baseline' }}
                        >
                          専用フォーマットをダウンロード
                        </Link>
                      </Typography>
                      <Typography variant="body2">
                        [更新]{' '}
                        <Link
                          component="button"
                          variant="body2"
                          onClick={onDownloadRegistered}
                          disabled={
                            isDownloadingRegistered ||
                            isDownloadRegisteredDisabled
                          }
                          sx={{
                            verticalAlign: 'baseline',
                            ...(isDownloadRegisteredDisabled && {
                              color: 'text.disabled',
                              pointerEvents: 'none',
                            }),
                          }}
                        >
                          アップロード済みの最新データをダウンロード
                        </Link>
                      </Typography>
                    </Box>
                    <Box component="li">
                      <Typography variant="body2" fontWeight="bold">
                        データを編集する
                      </Typography>
                      <Typography variant="body2">
                        ダウンロードしたCSVファイルを開き、追加・更新したい用語を入力してください。
                      </Typography>
                      <Typography
                        variant="caption"
                        component="p"
                        sx={{ mt: 0.5 }}
                      >
                        ※更新しない項目は削除せずに残してください。ファイルの内容がそのまま最新版として反映されます。
                      </Typography>
                    </Box>
                    <Box component="li">
                      <Typography variant="body2" fontWeight="bold">
                        ファイルをアップロードする
                      </Typography>
                      <Typography variant="body2">
                        編集したCSVファイルを下記よりアップロードしてください。
                      </Typography>
                    </Box>
                  </Box>
                </Alert>

                <Stack spacing={1}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    アップロードするCSVファイル
                  </Typography>
                  <Box {...getRootProps()} sx={dropzoneSx}>
                    <input {...getInputProps()} />
                    <CloudUploadIcon
                      sx={{
                        fontSize: 40,
                        color: isDragReject ? 'error.main' : 'action.active',
                      }}
                    />
                    {isDragReject ? (
                      <Typography variant="body2" color="error">
                        {dragFileCount > 1
                          ? 'アップロードできるファイルは1つだけです'
                          : 'CSVファイルのみアップロードできます'}
                      </Typography>
                    ) : (
                      <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        flexWrap="wrap"
                        justifyContent="center"
                      >
                        <Typography variant="body2" color="text.secondary">
                          ここにCSVファイルをドラッグ&ドロップ または
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={openFileDialog}
                          disabled={uploadMutation.isPending}
                        >
                          ファイルを選択
                        </Button>
                      </Stack>
                    )}
                  </Box>
                  {rejectionError && (
                    <Typography variant="body2" color="error">
                      {rejectionError}
                    </Typography>
                  )}
                </Stack>

                {selectedFile && (
                  <Stack spacing={1}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        px: 1,
                        py: 0.5,
                        alignSelf: 'flex-start',
                      }}
                    >
                      <InsertDriveFileIcon
                        fontSize="small"
                        sx={{ color: 'action.active' }}
                      />
                      <Typography
                        variant="body2"
                        color="text.primary"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {selectedFile.name}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedFile(null)
                          uploadMutation.reset()
                        }}
                        disabled={uploadMutation.isPending}
                        aria-label="ファイルを削除"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                    {uploadMutation.isError && (
                      <Stack spacing={0.5}>
                        <Typography variant="body2" color="error">
                          アップロードに失敗しました。
                        </Typography>
                        <Typography
                          variant="body2"
                          color="error"
                          sx={{ whiteSpace: 'pre-line' }}
                        >
                          {isHTTPError(uploadMutation.error)
                            ? uploadMutation.error.errors.join('\n')
                            : 'ネットワーク環境をご確認の上、再度アップロードしてください。'}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                )}
              </Stack>

              <Stack
                direction="row"
                spacing={2}
                justifyContent="flex-end"
                sx={{ pt: 3 }}
              >
                <Button
                  onClick={handleClose}
                  variant="outlined"
                  disabled={uploadMutation.isPending}
                >
                  閉じる
                </Button>
                <Button
                  onClick={handleExecuteClick}
                  color="primary"
                  variant="contained"
                  disabled={uploadMutation.isPending || !selectedFile}
                  startIcon={
                    uploadMutation.isPending ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : undefined
                  }
                >
                  {uploadMutation.isPending ? 'アップロード中...' : '実行'}
                </Button>
              </Stack>
            </>
          )}
        </Box>
      </Drawer>
      <UploadConfirmDialog
        open={confirmDialogOpen}
        fileName={selectedFile?.name}
        onConfirm={handleConfirmUpload}
        onCancel={handleCancelConfirm}
      />
    </>
  )
}
